// client/src/utils/api/config.js
import axios from 'axios';
import { getLogger } from '../logger';
import { getRequestId } from '../requestId';
import { sanitizeNullishStrings } from '../sanitizePayload';
import {
  clearServerNetworkError,
  reportRateLimited,
  reportServerError,
  setOffline,
  setOnline,
} from '../networkStatus';

/**
 * Main API instance for backend calls
 */
export const api = axios.create({
  baseURL: import.meta.env.VITE_SERVER_URL,
  withCredentials: true,
});

/**
 * Mailivo API instance for campaign calls
 */
export const mailivoApi = axios.create({
  baseURL: import.meta.env.VITE_MAILIVO_API_URL || import.meta.env.VITE_MAILIVO_URL,
  withCredentials: true,
});

const apiLog = getLogger('apiConfig');
const mailivoLog = getLogger('mailivoApi');
const pendingGetRequests = new Map();
const supersededRequestPromises = new Map();
const SUPERSEDED_REQUEST_REASON = "Superseded by newer request";

export const isCanceledError = (error) =>
  error?.code === "ERR_CANCELED" || error?.name === "CanceledError";

export const classifyApiError = (error) => {
  if (isCanceledError(error)) {
    return { kind: "canceled", status: null };
  }

  const status = error?.response?.status ?? null;
  if (status) {
    return { kind: "http", status };
  }

  if (error?.request) {
    return { kind: "network", status: null };
  }

  return { kind: "unknown", status: null };
};

const stableSerialize = (value) => {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(",")}]`;
  }

  const keys = Object.keys(value).sort();
  return `{${keys
    .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
    .join(",")}}`;
};

const toRequestIdentity = (config) => {
  const method = (config?.method || "get").toUpperCase();
  const url = config?.url || "";
  const params = stableSerialize(config?.params || {});
  return `${method}:${url}:${params}`;
};

const createDeferred = () => {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
};

const settlePendingGetRequest = (config, { response, error } = {}) => {
  const cancellationKey = config?.metadata?.cancellationKey;
  if (!cancellationKey) return;

  const pending = pendingGetRequests.get(cancellationKey);
  if (pending?.requestId === config?.metadata?.requestId) {
    if (response) {
      pending.resolve(response);
    } else {
      pending.reject(error || new Error("Request failed"));
    }
    pendingGetRequests.delete(cancellationKey);
  }
};

const attachAutoRequestCancellation = (config, options = {}) => {
  const { autoCancelGetRequests = true } = options;
  if (!autoCancelGetRequests) return config;

  const method = (config?.method || "get").toLowerCase();
  if (method !== "get" || config?.disableAutoCancel === true) {
    return config;
  }

  const cancellationKey = toRequestIdentity(config);
  const deferred = createDeferred();
  const existing = pendingGetRequests.get(cancellationKey);
  if (existing) {
    const supersedingPromise = deferred.promise.finally(() => {
      supersededRequestPromises.delete(existing.requestId);
    });
    supersededRequestPromises.set(existing.requestId, supersedingPromise);
    existing.controller.abort(SUPERSEDED_REQUEST_REASON);
  }

  const controller = new AbortController();
  if (config?.signal) {
    if (config.signal.aborted) {
      controller.abort(config.signal.reason || "Request already aborted");
    } else {
      config.signal.addEventListener(
        "abort",
        () => controller.abort(config.signal.reason || "Request aborted"),
        { once: true }
      );
    }
  }

  config.signal = controller.signal;
  config.metadata = {
    ...(config.metadata || {}),
    cancellationKey,
  };

  pendingGetRequests.set(cancellationKey, {
    controller,
    requestId: config.metadata?.requestId,
    responsePromise: deferred.promise,
    resolve: deferred.resolve,
    reject: deferred.reject,
  });
  return config;
};

const buildRetryRequest = (instance, config) => {
  if (!config) return null;
  return () =>
    instance.request({
      ...config,
      metadata: undefined,
    });
};

const attachRequestMeta = (config, { attachRequestId = true } = {}) => {
  const requestId = config.headers?.["x-request-id"] || getRequestId();
  if (attachRequestId) {
    config.headers = config.headers || {};
    config.headers["x-request-id"] = requestId;
  }
  config.metadata = {
    requestId,
    startTime: performance.now(),
  };
  return config;
};

const logRequest = (log, config, options) => {
  attachRequestMeta(config, options);
  attachAutoRequestCancellation(config, options);
  if (config?.data) {
    config.data = sanitizeNullishStrings(config.data);
  }
  if (config?.params) {
    config.params = sanitizeNullishStrings(config.params);
  }
  log.info(
    `[${log.context}:logRequest] > [Request]: ${config.method?.toUpperCase()} ${config.url} > [Response]: pending`,
    {
      requestId: config.metadata?.requestId,
      method: config.method?.toUpperCase(),
      url: config.url,
    }
  );
  return config;
};

const logResponse = (log, response) => {
  settlePendingGetRequest(response?.config, { response });
  const { metadata } = response.config || {};
  const durationMs = metadata?.startTime ? Math.round(performance.now() - metadata.startTime) : undefined;
  setOnline();
  clearServerNetworkError();
  log.info(
    `[${log.context}:logResponse] > [Request]: ${response.config?.method?.toUpperCase() || "REQUEST"} ${response.config?.url || ""} > [Response]: status=${response.status}`,
    {
      requestId: metadata?.requestId,
      status: response.status,
      durationMs,
      url: response.config?.url,
    }
  );
  return response;
};

const logError = (log, error) => {
  const metadata = error?.config?.metadata || {};
  const coalescedPromise = metadata?.requestId
    ? supersededRequestPromises.get(metadata.requestId)
    : null;
  const pendingForKey = metadata?.cancellationKey
    ? pendingGetRequests.get(metadata.cancellationKey)
    : null;
  const supersededByNewerRequest =
    Boolean(pendingForKey) && pendingForKey.requestId !== metadata.requestId;

  if (error?.code === "ERR_CANCELED") {
    if (coalescedPromise || supersededByNewerRequest) {
      const promiseToAwait = coalescedPromise || pendingForKey.responsePromise;
      const coalescedToRequestId = pendingForKey?.requestId;
      log.info(
        `[${log.context}:logError] > [Request]: ${error?.config?.method?.toUpperCase() || "REQUEST"} ${error?.config?.url || ""} > [Response]: canceled > [Comment]: coalesced_to_request=${coalescedToRequestId || "pending"}`,
        {
          requestId: metadata?.requestId,
          coalescedToRequestId,
          url: error?.config?.url,
        }
      );
      supersededRequestPromises.delete(metadata.requestId);
      return promiseToAwait;
    }

    settlePendingGetRequest(error?.config, { error });
    log.info(
      `[${log.context}:logError] > [Request]: ${error?.config?.method?.toUpperCase() || "REQUEST"} ${error?.config?.url || ""} > [Response]: canceled`,
      {
        requestId: metadata?.requestId,
        url: error?.config?.url,
      }
    );
    return Promise.reject(error);
  }

  settlePendingGetRequest(error?.config, { error });
  const durationMs = metadata?.startTime ? Math.round(performance.now() - metadata.startTime) : undefined;
  const status = error?.response?.status;
  const url = error?.config?.url;

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    setOffline();
  } else {
    setOnline();
  }

  if (status === 429) {
    reportRateLimited({
      retryAfterHeader: error?.response?.headers?.["retry-after"],
      retryRequest: buildRetryRequest(log === mailivoLog ? mailivoApi : api, error?.config),
      url,
    });
  } else if (status >= 500 && status <= 599) {
    reportServerError({
      status,
      url,
      retryRequest: buildRetryRequest(log === mailivoLog ? mailivoApi : api, error?.config),
    });
  }

  log.error(
    `[${log.context}:logError] > [Request]: ${error?.config?.method?.toUpperCase() || "REQUEST"} ${error?.config?.url || ""} > [Response]: error=${error?.message || "unknown"} > [Comment]: status=${error?.response?.status || "unknown"}`,
    {
      requestId: metadata?.requestId,
      status,
      durationMs,
      url,
    }
  );
  return Promise.reject(error);
};

api.interceptors.request.use((config) => logRequest(apiLog, config));
api.interceptors.response.use(
  (response) => logResponse(apiLog, response),
  (error) => logError(apiLog, error)
);

mailivoApi.interceptors.request.use((config) =>
  logRequest(mailivoLog, config, { attachRequestId: false, autoCancelGetRequests: false })
);
mailivoApi.interceptors.response.use(
  (response) => logResponse(mailivoLog, response),
  (error) => logError(mailivoLog, error)
);

/**
 * Centralized error handler for API requests
 * @param {Error} error - The error object
 * @param {string} message - Custom error message
 */
export const handleRequestError = (error, message) => {
  const { kind } = classifyApiError(error);
  if (kind === "canceled") {
    apiLog.info(
      `[apiConfig:handleRequestError] > [Request]: apiCall > [Response]: canceled > [Comment]: ${message}`
    );
    throw error;
  }

  apiLog.error(
    `[apiConfig:handleRequestError] > [Request]: apiCall > [Response]: error=${message} ${error?.message || "unknown"}`
  );
  throw error;
};

// ============================================================================
// URL HELPER FUNCTIONS
// ============================================================================

/**
 * Get full server URL
 * @returns {string} Server base URL
 */
export const getServerUrl = () => {
  return import.meta.env.VITE_SERVER_URL;
};

/**
 * Get full avatar URL from relative path
 * @param {string} avatarPath - Relative avatar path (e.g., 'uploads/avatars/avatar.jpg')
 * @returns {string} Full avatar URL
 */
export const getAvatarUrl = (avatarPath) => {
  if (!avatarPath) return null;
  // If already a full URL, return as is
  if (avatarPath.startsWith('http')) return avatarPath;
  // Otherwise construct full URL
  return `${getServerUrl()}/${avatarPath}`;
};

/**
 * Get full upload URL from relative path
 * @param {string} uploadPath - Relative upload path
 * @returns {string} Full upload URL
 */
export const getUploadUrl = (uploadPath) => {
  if (!uploadPath) return null;
  if (uploadPath.startsWith('http')) return uploadPath;
  return `${getServerUrl()}/${uploadPath}`;
};

/**
 * Get Mailivo API URL
 * @returns {string} Mailivo API base URL
 */
export const getMailivoApiUrl = () => {
  return import.meta.env.VITE_MAILIVO_API_URL || 
         import.meta.env.VITE_MAILIVO_URL || 
         'https://mailivo.landivo.com/api';
};
